import React from 'react';

const mask = (v) => (!v ? '' : v.length <= 6 ? '*'.repeat(v.length) : `${v.slice(0, 3)}***${v.slice(-3)}`);

const REQUIRED = 'required';
const RECOMMENDED = 'recommended';
const OPTIONAL = 'optional';

const badge = (present, tier) => {
  if (present) return { text: 'SET', cls: 'bg-emerald-100 text-emerald-800' };
  if (tier === REQUIRED) return { text: 'MISSING', cls: 'bg-red-100 text-red-800' };
  if (tier === RECOMMENDED) return { text: 'RECOMMENDED', cls: 'bg-amber-100 text-amber-800' };
  return { text: 'OPTIONAL', cls: 'bg-gray-200 text-gray-700' };
};

/**
 * Deployment env probe — masked view of env vars per tier.
 * Reachable at /health/env (public).
 */
export default function EnvHealthPage() {
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const ga4 = process.env.REACT_APP_GA4_ID || '';
  const maps = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    fetch(`${backend}/api/health/env-check`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ ok: false, error: 'unreachable' }));
  }, [backend]);

  const Row = ({ label, present, value, tier }) => {
    const b = badge(present, tier);
    return (
      <div className="flex items-start justify-between py-2 border-b border-border last:border-0" data-testid={`env-row-${label}`}>
        <div className="min-w-0 flex-1 pr-3">
          <div className="font-mono text-sm break-all">{label}</div>
          {value ? <div className="font-mono text-xs text-muted-foreground mt-0.5 break-all">{value}</div> : null}
        </div>
        <span data-testid={`env-badge-${label}`} className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${b.cls}`}>{b.text}</span>
      </div>
    );
  };

  const Section = ({ title, subtitle, rows, tier, previews, scope }) => (
    <section className="bg-white border border-border rounded-xl p-5" data-testid={`env-section-${scope}-${tier}`}>
      <h2 className="font-semibold">{title}</h2>
      {subtitle ? <p className="text-xs text-muted-foreground mb-3">{subtitle}</p> : null}
      {Object.entries(rows).map(([k, v]) => (
        <Row key={k} label={k} present={v} value={previews?.[k]} tier={tier} />
      ))}
    </section>
  );

  const frontendRequired = { REACT_APP_BACKEND_URL: !!backend };
  const frontendRecommended = { REACT_APP_GA4_ID: !!ga4, REACT_APP_GOOGLE_MAPS_API_KEY: !!maps };
  const frontendPreviews = {
    REACT_APP_BACKEND_URL: backend,
    REACT_APP_GA4_ID: ga4,
    REACT_APP_GOOGLE_MAPS_API_KEY: mask(maps),
  };

  return (
    <div className="min-h-screen bg-muted/30 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-serif font-bold" data-testid="env-health-title">Deployment env probe</h1>
          <p className="text-sm text-muted-foreground">Masked view of environment configuration. Only the <strong>required</strong> section must be green — <strong>recommended</strong> unlocks optional features, <strong>optional</strong> can safely stay unset.</p>
        </div>

        <div className={`rounded-xl border p-4 text-sm ${data?.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`} data-testid="env-overall">
          <strong>Overall status:</strong>{' '}
          {data === null ? 'Checking…' : data?.error ? `Backend unreachable (${data.error})` : data?.ok ? 'All required backend variables are set. App is ready to serve traffic.' : 'One or more REQUIRED backend variables are missing. Fix these before going live.'}
        </div>

        {/* FRONTEND */}
        <Section
          title="Frontend — required"
          subtitle="Baked into the JS bundle at build time. Change requires a redeploy."
          rows={frontendRequired}
          tier={REQUIRED}
          previews={frontendPreviews}
          scope="frontend"
        />
        <Section
          title="Frontend — recommended"
          subtitle="Enables analytics and live rider map. App works without them."
          rows={frontendRecommended}
          tier={RECOMMENDED}
          previews={frontendPreviews}
          scope="frontend"
        />

        {/* BACKEND */}
        {data && data.required && (
          <Section
            title="Backend — required"
            subtitle="App will not function without these. MONGO_URL & DB_NAME are auto-injected by Emergent."
            rows={data.required}
            tier={REQUIRED}
            previews={data.previews}
            scope="backend"
          />
        )}
        {data && data.recommended && (
          <Section
            title="Backend — recommended"
            subtitle="Enables emails (Resend), AI features, and strict CORS."
            rows={data.recommended}
            tier={RECOMMENDED}
            previews={data.previews}
            scope="backend"
          />
        )}
        {data && data.optional && (
          <Section
            title="Backend — optional overrides"
            subtitle="Only set if you want to override the default seeded admin credentials. Leaving these missing uses the built-in defaults from /app/DEPLOY.md."
            rows={data.optional}
            tier={OPTIONAL}
            previews={data.previews}
            scope="backend"
          />
        )}

        <p className="text-xs text-muted-foreground">
          Only booleans and masked previews are exposed. Full secret values are never returned.
        </p>
      </div>
    </div>
  );
}
